import { paymentReleaseErrorMessages } from 'src/constants/invoice/constants'
import { PaymentReleaseErrorKind } from 'src/constants/invoice/enums'
import type { ApiErrorBody, PaymentReleaseFailure } from 'src/interfaces/invoice'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'

/** The backend error NAMES this client recognizes, matched against the body's `error` field. */
const NAMED_ERROR_KINDS: PaymentReleaseErrorKind[] = [
  PaymentReleaseErrorKind.InvoiceNotFound,
  PaymentReleaseErrorKind.InvalidReleaseDate,
  PaymentReleaseErrorKind.ReleaseInProgress,
  PaymentReleaseErrorKind.AutoReleaseOnFundingEnabled,
  PaymentReleaseErrorKind.NoPaymentsAwaitingRelease,
  PaymentReleaseErrorKind.NoScheduledRelease,
]

/** Sending the same request again cannot succeed — the modal offers only Close. */
const TERMINAL_KINDS: PaymentReleaseErrorKind[] = [
  PaymentReleaseErrorKind.Permission,
  PaymentReleaseErrorKind.InvoiceNotFound,
  PaymentReleaseErrorKind.ReleaseInProgress,
  PaymentReleaseErrorKind.AutoReleaseOnFundingEnabled,
  PaymentReleaseErrorKind.NoPaymentsAwaitingRelease,
  PaymentReleaseErrorKind.NoScheduledRelease,
]

/**
 * The row the admin acted on no longer matches the server: the list is refetched rather than
 * leaving a stale row with an error over it.
 */
const STALE_ROW_KINDS: PaymentReleaseErrorKind[] = [
  PaymentReleaseErrorKind.InvoiceNotFound,
  PaymentReleaseErrorKind.ReleaseInProgress,
  PaymentReleaseErrorKind.NoPaymentsAwaitingRelease,
  PaymentReleaseErrorKind.NoScheduledRelease,
]

interface ThrownApiError {
  status?: number
  payload?: ApiErrorBody | null
  message?: string
}

const resolveKind = (error: unknown): PaymentReleaseErrorKind => {
  const thrown = error as ThrownApiError | null | undefined
  const errorName = thrown?.payload?.error
  const namedKind = NAMED_ERROR_KINDS.find((kind) => {
    return kind === errorName
  })

  if (namedKind) {
    return namedKind
  }

  const status = thrown?.status ?? thrown?.payload?.statusCode

  if (status === 400) {
    return PaymentReleaseErrorKind.Validation
  }

  // `callSecureApi` throws a bare `Error('Unauthorized')`, with no status, when there is no token.
  if (status === 401 || (status === undefined && thrown?.message === 'Unauthorized')) {
    return PaymentReleaseErrorKind.Auth
  }

  if (status === 403) {
    return PaymentReleaseErrorKind.Permission
  }

  return PaymentReleaseErrorKind.Unknown
}

/**
 * Classifies a thrown `callSecureApi` error by its error NAME (then its status), never by its
 * message text. An unrecognized error keeps the server's own message so nothing is hidden.
 */
export const classifyPaymentReleaseError = (
  error: unknown,
  fallbackMessage: string,
): PaymentReleaseFailure => {
  const kind = resolveKind(error)
  const message =
    kind === PaymentReleaseErrorKind.Unknown
      ? resolveErrorMessage(error, fallbackMessage)
      : paymentReleaseErrorMessages[kind]

  return {
    kind,
    message,
    isRetryable: !TERMINAL_KINDS.includes(kind),
    shouldRefetch: STALE_ROW_KINDS.includes(kind),
  }
}
